/**
 * InfiniteDocGrid — канон-таблиця списків документів (Норма #29, Закон
 * дизайну #1): ag-Grid Infinite + серверна пагінація/сортування, дизайн
 * як список номенклатури (ag-theme-quartz grid, збереження ширин колонок).
 *
 * Page: викликає useInfiniteGridDatasource сам (щоб мати total для
 * лічильника у заголовку) і передає сюди datasource + columnDefs.
 * refreshRef — мутації сторінки кличуть refreshRef.current?.() замість
 * react-query invalidate (datasource поза кешем react-query).
 */
import type { MutableRefObject, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi, GridReadyEvent, IDatasource, RowClassParams, RowStyle } from 'ag-grid-community';
import uiPrefs from '@/store/uiPrefs';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import { GridMoreRightButton, useAgMoreRight } from '@/shared/components/GridMoreRight';
import { arrangeAgCols } from '@/shared/components/docStatusCols';
import { useConfirm } from '@/components/ConfirmProvider';
import { showToastImperative } from '@/components/ToastProvider';
import ColumnSettingsButton, {
  applyColPrefs, clearColPrefs, loadColPrefs, saveColPrefs, subscribeColPrefs,
  SELECT_COL_ID, type ColItem, type ColPrefs,
} from '@/shared/components/ColumnSettings';

// ч.1332 (запит власника «зберігай сортування в колонках»): обране сортування списку — у профілі
// користувача, як порядок і ширини колонок; «скинути» в налаштуваннях колонок його прибирає.
const SORT_PREFIX = 'ag-sort:';
type AgSort = { colId: string; sort: 'asc' | 'desc' };

function readSort(key: string): AgSort | null {
  try {
    const v = uiPrefs.get<AgSort>(key);
    return v && typeof v.colId === 'string' && (v.sort === 'asc' || v.sort === 'desc') ? v : null;
  } catch { return null; }
}

function gridSort<T>(api: GridApi<T>): AgSort | null {
  const c = api.getColumnState().find((x) => x.sort === 'asc' || x.sort === 'desc');
  return c ? { colId: c.colId, sort: c.sort as 'asc' | 'desc' } : null;
}

interface Props<T> {
  datasource: IDatasource;
  columnDefs: ColDef<T>[];
  /** Ключ збереження ширин колонок (localStorage). */
  storageKey: string;
  onRowDoubleClicked?: (row: T) => void;
  /** ч.1217: одинарний клік по рядку (напр. попап звʼязаних документів у зведених журналах).
   *  Клік по посиланню, кнопці чи полю всередині рядка сюди НЕ доходить. */
  onRowClicked?: (row: T) => void;
  getRowStyle?: (p: RowClassParams<T>) => RowStyle | undefined;
  refreshRef?: MutableRefObject<(() => void) | null>;
  /** ч.734: мультивибір рядків (чекбокс-колонка сторінки з checkboxSelection). */
  rowSelection?: 'single' | 'multiple';
  onSelectionChanged?: (rows: T[]) => void;
  /** Стабільний id рядка — потрібен ag-Grid Infinite для збереження вибору. */
  getRowId?: (row: T) => string;
  /** Доступ до GridApi зі сторінки (напр. «позначити всі завантажені»). */
  apiRef?: MutableRefObject<GridApi<T> | null>;
  /** ч.748: додаткові посилання/кнопки ЛІВОРУЧ від шестерінки колонок. */
  headerExtra?: ReactNode;
  /** ч.794: слот ЗЛІВА у рядку шестерінки (підсумок, «позначити усі»). */
  headerLeft?: ReactNode;
  /** ч.766: рядки, які не можна відібрати (галка не ставиться). */
  isRowSelectable?: (row: T) => boolean;
  /** ч.774: «×» видалення документа прямо зі списку. canDelete за замовч.: !posted &&
   *  !deletion_mark; `whyNot` — підказка на неактивному «×» (за замовч. — «проведений…»).
   *  Після видалення — refreshInfiniteCache. ч.1316: «×» стоїть поруч зі «скопіювати» в одній
   *  закріпленій праворуч колонці дій (раніше — окрема остання колонка, часто за краєм екрана). */
  deleteRow?: {
    remove: (id: string) => Promise<unknown>;
    canDelete?: (row: T) => boolean;
    whyNot?: (row: T) => string;
  };
  /** ч.1267: «скопіювати» — новий документ з даними цього рядка (колонка дій праворуч). */
  copyRow?: (row: T) => void;
}

/** ч.1217: клік по інтерактивному елементу рядка (посилання «відкрити», «×» видалення,
 *  галочка) — це дія самого елемента, а не «клік по рядку». */
export function isInteractiveClick(target: EventTarget | null | undefined): boolean {
  const el = target as { closest?: (sel: string) => unknown } | null | undefined;
  return !!el?.closest?.('a, button, input, select, label');
}

// Стабільний id колонки для персисту налаштувань (colId → field → c<i>).
function colIdOf<T>(c: ColDef<T>, i: number): string {
  return (c.colId as string) ?? (c.field as string) ?? `c${i}`;
}

// Людська назва колонки для попапа «колонки» (статусні — без headerName).
function colLabelOf<T>(c: ColDef<T>, id: string): string {
  const SPECIAL: Record<string, string> = {
    posted: 'Проведено', _att: 'Вкладення', sign_control: 'Підпис',
    _badge: 'Бейдж «Імпорт»',
    _actions: 'Дії: скопіювати, видалити',
  };
  if (SPECIAL[id]) return SPECIAL[id];
  const h = c.headerName;
  if (h) return h;
  if (c.headerTooltip) return String(c.headerTooltip);
  return String(c.field ?? id);
}

export default function InfiniteDocGrid<T>({
  datasource, columnDefs, storageKey, onRowDoubleClicked, onRowClicked, getRowStyle, refreshRef,
  rowSelection = 'single', onSelectionChanged, getRowId, apiRef, headerExtra, headerLeft, isRowSelectable, deleteRow, copyRow,
}: Props<T>) {
  const gridApiRef = useRef<GridApi<T> | null>(null);
  const colW = useGridColumnWidths(storageKey);
  // Налаштування порядку/показу колонок (рішення власника 2026-08-26).
  const [prefs, setPrefs] = useState<ColPrefs | null>(() => loadColPrefs(storageKey));
  // Серверні prefs приходять ПІСЛЯ mount (uiPrefs.init в AppLayout) —
  // підписка перерендерює таблицю профільними налаштуваннями.
  useEffect(
    () => subscribeColPrefs(storageKey, () => setPrefs(loadColPrefs(storageKey))),
    [storageKey],
  );
  // Сортування: перше — з профілю ДО першого запиту (initialState), далі — підписка: профіль
  // приходить із сервера після показу (інший компʼютер), «скинути» його прибирає.
  const sortPrefKey = SORT_PREFIX + storageKey;
  const [initialSort] = useState(() => readSort(sortPrefKey));
  useEffect(() => uiPrefs.subscribe(sortPrefKey, () => {
    const api = gridApiRef.current;
    if (!api) return;
    const want = readSort(sortPrefKey);
    const now = gridSort(api);
    if (want?.colId === now?.colId && want?.sort === now?.sort) return;
    api.applyColumnState({ state: want ? [want] : [], defaultState: { sort: null } });
  }), [sortPrefKey]);

  // Статус-колонки (проведено/вкладення/підпис) — іконки + канонічний порядок
  // одразу після бейджа міграції (рішення власника 2026-08-23). Це ДЕФОЛТ;
  // поверх нього — користувацькі prefs (порядок + приховання).
  const confirm = useConfirm();
  const baseCols = useMemo(() => {
    const arranged = arrangeAgCols(columnDefs);
    // Стабільні colId для персисту: бейдж міграції (headerName '' + width 44)
    // отримує '_badge', решта без field/colId — позиційний c<i>.
    const cols = arranged.map((c, i) => {
      // ч.1033: колонку галочок не можна «обігнати» перетягуванням іншої.
      if (c.colId === SELECT_COL_ID) return { lockPosition: 'left' as const, suppressMovable: true, ...c };
      if (c.colId || c.field) return c;
      const isBadge = c.headerName === '' && c.width === 44;
      return { ...c, colId: isBadge ? '_badge' : `c${i}` };
    });
    // ч.1316 (рішення власника): «скопіювати» і «×» видалення — в ОДНІЙ колонці дій, закріпленій
    // праворуч. У списках з багатьма колонками остання колонка за краєм екрана (сітка малює лише
    // видимі), а дії мають бути під рукою. «×», який зараз не можна натиснути (проведений
    // документ), видно неактивним із поясненням — як кнопки на плашці документа (ч.702).
    if (copyRow || deleteRow) {
      const canDel = deleteRow?.canDelete ?? ((r: T) => {
        const x = r as { posted?: boolean; deletion_mark?: boolean };
        return !x.posted && !x.deletion_mark;
      });
      const whyNot = deleteRow?.whyNot ?? ((r: T) => {
        const x = r as { posted?: boolean; deletion_mark?: boolean };
        if (x.posted) return 'Проведений документ не видаляється — спершу розпровести';
        if (x.deletion_mark) return 'Документ позначено на видалення';
        return 'Цей документ не можна видалити';
      });
      cols.push({
        colId: '_actions', headerName: '', width: (copyRow ? 34 : 0) + (deleteRow ? 34 : 0) + 8,
        sortable: false, resizable: false,
        pinned: 'right', lockPinned: true, lockPosition: 'right',
        suppressMovable: true, cellStyle: { textAlign: 'center', padding: 0 },
        cellRenderer: (p: { data?: T; api: GridApi<T> }) => !p.data ? null : (
          <RowActions row={p.data} copyRow={copyRow} remove={deleteRow?.remove}
            canDelete={deleteRow ? canDel : undefined} whyNot={whyNot} confirm={confirm}
            onDeleted={() => p.api.refreshInfiniteCache()} />
        ),
      } as ColDef<T>);
    }
    return cols;
  }, [columnDefs, deleteRow, copyRow, confirm]);

  const colDefs = useMemo(
    () => applyColPrefs(baseCols, prefs, (c) => colIdOf(c, baseCols.indexOf(c))),
    [baseCols, prefs],
  );

  const items: ColItem[] = useMemo(() => {
    // Порядок у попапі = поточний порядок відображення + приховані у хвості
    // (на своїх дефолтних місцях).
    const ordered = applyColPrefs(baseCols, prefs && { ...prefs, hidden: [] }, (c) => colIdOf(c, baseCols.indexOf(c)));
    // ч.1033: колонку галочок не показуємо у «колонки» — її не можна ні
    // сховати, ні пересунути (завжди перша, див. applyColPrefs).
    return ordered
      .map((c) => {
        const id = colIdOf(c, baseCols.indexOf(c));
        return { id, label: colLabelOf(c, id) };
      })
      .filter((it) => it.id !== SELECT_COL_ID);
  }, [baseCols, prefs]);

  if (refreshRef) {
    refreshRef.current = () => gridApiRef.current?.refreshInfiniteCache();
  }

  // ч.1316: «ще колонки →» над таблицею (раніше лише в DataTable) — GridMoreRight.tsx.
  const more = useAgMoreRight<T>();
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.6rem', marginBottom: 2 }}>
        {headerLeft && <span style={{ marginRight: 'auto' }}>{headerLeft}</span>}
        <GridMoreRightButton more={more} />
        {headerExtra}
        <ColumnSettingsButton
          items={items}
          hidden={prefs?.hidden ?? []}
          onChange={(order, hidden) => {
            const next = { order, hidden };
            saveColPrefs(storageKey, next);
            setPrefs(next);
          }}
          onReset={() => {
            clearColPrefs(storageKey); setPrefs(null);
            try { uiPrefs.remove(sortPrefKey); } catch { /* приватний режим — не критично */ }
          }}
        />
      </div>
      <div className="ag-theme-quartz grid">
        <AgGridReact<T>
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={100}
          columnDefs={colDefs}
          initialState={initialSort ? { sort: { sortModel: [initialSort] } } : undefined}
          onSortChanged={(e) => {
            if (e.source !== 'uiColumnSorted') return;   // лише клік користувача
            const s = gridSort(e.api);
            try {
              if (s) uiPrefs.set(sortPrefKey, s); else uiPrefs.remove(sortPrefKey);
            } catch { /* приватний режим — не критично */ }
          }}
          onGridReady={(e: GridReadyEvent<T>) => {
            gridApiRef.current = e.api; colW.applyTo(e.api);
            if (apiRef) apiRef.current = e.api;
            more.gridProps.onGridReady(e);
          }}
          onColumnResized={(e) => { colW.onColumnResized(e); more.check(e.api); }}
          onBodyScroll={more.gridProps.onBodyScroll}
          onGridSizeChanged={more.gridProps.onGridSizeChanged}
          onDisplayedColumnsChanged={more.gridProps.onDisplayedColumnsChanged}
          onFirstDataRendered={more.gridProps.onFirstDataRendered}
          onColumnMoved={(e) => {
            // Drag заголовків мишею → зберігаємо порядок як prefs.
            if (!e.finished || e.source !== 'uiColumnMoved') return;
            const order = e.api.getColumnState().map((c) => c.colId);
            const next = { order, hidden: prefs?.hidden ?? [] };
            saveColPrefs(storageKey, next);
            setPrefs(next);
          }}
          onRowDoubleClicked={(e) => e.data && onRowDoubleClicked?.(e.data)}
          onRowClicked={onRowClicked
            ? (e) => { if (e.data && !isInteractiveClick(e.event?.target)) onRowClicked(e.data); }
            : undefined}
          rowSelection={rowSelection}
          isRowSelectable={isRowSelectable ? (n) => (n.data ? isRowSelectable(n.data as T) : false) : undefined}
          suppressRowClickSelection={rowSelection === 'multiple'}
          onSelectionChanged={onSelectionChanged
            ? (e) => onSelectionChanged(e.api.getSelectedRows() as T[])
            : undefined}
          getRowId={getRowId ? (p) => getRowId(p.data as T) : undefined}
          getRowStyle={getRowStyle}
        />
      </div>
    </div>
  );
}

/** ч.1316: вміст колонки дій рядка — «скопіювати» і «×» поруч. Неактивний «×» пояснює, чому
 *  видалити не можна. */
export function RowActions<T>({ row, copyRow, remove, canDelete, whyNot, confirm, onDeleted }: {
  row: T;
  copyRow?: (row: T) => void;
  remove?: (id: string) => Promise<unknown>;
  canDelete?: (row: T) => boolean;
  whyNot: (row: T) => string;
  confirm: (message: string) => Promise<boolean>;
  onDeleted: () => void;
}) {
  const r = row as T & { id: string; document_number?: string };
  const btn = { padding: '0 0.4rem', lineHeight: 1.2 };
  const allowed = !!remove && !!canDelete && canDelete(row);
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {copyRow && (
        <button type="button" data-testid="row-copy"
          title="Скопіювати: новий документ з тими самими даними"
          style={btn}
          onClick={(e) => { e.stopPropagation(); copyRow(row); }}>⧉</button>
      )}
      {remove && (
        <button type="button" className="danger" data-testid="row-delete"
          disabled={!allowed}
          title={allowed ? 'Видалити документ' : whyNot(row)}
          style={btn}
          onClick={(e) => {
            e.stopPropagation();
            void confirm(`Видалити документ${r.document_number ? ' № ' + r.document_number : ''}?`).then(async (ok) => {
              if (!ok) return;
              try {
                await remove(r.id);
                onDeleted();
              } catch (err) {
                const d = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
                showToastImperative({ type: 'error', message: typeof d === 'string' ? d : 'Не вдалося видалити документ' });
              }
            });
          }}>×</button>
      )}
    </span>
  );
}

/** Лічильник (total) у заголовку — канон списку номенклатури. */
export function TitleTotal({ total }: { total: number | null }) {
  if (total === null) return null;
  return (
    <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
      ({total})
    </span>
  );
}
